'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, Download, UserPlus, MessageCircle, Edit2, Trash2, AlertTriangle, ChevronDown, ChevronUp, ChevronsUpDown, X, Check, Eye, Link2, Zap, Loader2, RefreshCw } from 'lucide-react';
import StageBadge from '@/components/ui/StageBadge';
import SourceBadge from '@/components/ui/SourceBadge';
import { toast } from 'sonner';
import { useRealtimeLeads, type Lead, type LeadStage } from '@/hooks/useRealtimeLeads';
import LeadSourceModal from './LeadSourceModal';
import { createClient } from '@/lib/supabase/client';

const STAGES: LeadStage[] = ['New', 'Contacted', 'Interested', 'Counseling Scheduled', 'Applied', 'Converted', 'Lost'];

// Source filter options — includes checkpoint-based filters
const SOURCES = [
  { value: 'All Sources', label: 'All Sources' },
  { value: 'MBBS Lighthouse', label: 'MBBS Lighthouse' },
  { value: 'GMC', label: 'GMC' },
  { value: 'Neet Navigator', label: 'Neet Navigator' },
  { value: 'checkpoint_gmc', label: '✓ Checkpoint: GMC' },
  { value: 'checkpoint_services', label: '✓ Checkpoint: Services' },
  { value: 'checkpoint_report', label: '✓ Checkpoint: Report' },
  { value: 'checkpoint_insight', label: '✓ Checkpoint: Insight' },
  { value: 'checkpoint_support', label: '✓ Checkpoint: Support' },
];

// Checkpoint filter values (for special DB-level filtering)
const CHECKPOINT_FILTERS = ['checkpoint_gmc', 'checkpoint_services', 'checkpoint_report', 'checkpoint_insight', 'checkpoint_support', 'checkpoint_fresh'];

// DRIP_CAMPAIGNS is now loaded dynamically from campaign_drip_sequences table

function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\s/g, '');
  if (cleaned.startsWith('+91')) return cleaned;
  if (cleaned.startsWith('91') && cleaned.length > 10) return `+${cleaned}`;
  return `+91${cleaned}`;
}

export default function LeadManagementClient() {
  const { leads, loading, error, updateLeadStage, refetch } = useRealtimeLeads();

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<LeadStage | 'All'>('All');
  const [sourceFilter, setSourceFilter] = useState('All Sources');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sortCol, setSortCol] = useState<keyof Lead>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [stageDropdownId, setStageDropdownId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);

  // Checkpoint sync state
  const [syncLoading, setSyncLoading] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncMaxPages, setSyncMaxPages] = useState(10);
  const [syncResult, setSyncResult] = useState<{
    total_fetched?: number;
    total_upserted?: number;
    pages_fetched?: number;
    errors?: string[];
    message?: string;
  } | null>(null);
  const [lastAutoSync, setLastAutoSync] = useState<Date | null>(null);
  const [nextSyncIn, setNextSyncIn] = useState<number>(15 * 60); // seconds until next sync

  // Bulk enroll state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollResult, setEnrollResult] = useState<{ enrolled?: number; queued?: number; errors?: string[] } | null>(null);
  const [dripCampaigns, setDripCampaigns] = useState<{ id: string; name: string }[]>([]);
  const [dripCampaignsLoading, setDripCampaignsLoading] = useState(false);

  // Add / Edit lead state
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [leadFormLoading, setLeadFormLoading] = useState(false);
  const emptyLeadForm = {
    name: '', phone: '', email: '', neetScore: '', source: 'Neet Navigator',
    stage: 'New' as LeadStage, counselor: '', budget: '', location: 'India',
  };
  const [leadForm, setLeadForm] = useState(emptyLeadForm);

  // Deduplicate state
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [showDedupeConfirm, setShowDedupeConfirm] = useState(false);

  // Fetch distinct campaigns from DB when enroll modal opens
  useEffect(() => {
    if (!showEnrollModal) return;
    const supabase = createClient();
    setDripCampaignsLoading(true);
    supabase
      .from('campaign_drip_sequences')
      .select('campaign_id, campaign_name')
      .order('campaign_name', { ascending: true })
      .then(({ data, error: fetchErr }) => {
        if (!fetchErr && data) {
          // Deduplicate by campaign_id
          const seen = new Set<string>();
          const unique = data
            .filter((row) => {
              if (seen.has(row.campaign_id)) return false;
              seen.add(row.campaign_id);
              return true;
            })
            .map((row) => ({ id: row.campaign_id, name: row.campaign_name }));
          setDripCampaigns(unique);
        }
        setDripCampaignsLoading(false);
      });
  }, [showEnrollModal]);

  const perPage = 8;

  // For checkpoint filters, we need to filter leads that have that checkpoint set
  const filtered = useMemo(() => {
    let data = leads.filter((l) => {
      const matchSearch =
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.phone.includes(search) ||
        formatPhone(l.phone).includes(search) ||
        l.email.toLowerCase().includes(search.toLowerCase());
      const matchStage = stageFilter === 'All' || l.stage === stageFilter;

      let matchSource = true;
      if (sourceFilter !== 'All Sources') {
        if (CHECKPOINT_FILTERS.includes(sourceFilter)) {
          if (sourceFilter === 'checkpoint_fresh') {
            // Fresh = leads with NO checkpoints set
            matchSource = !l.checkpointGmc && !l.checkpointServices && !l.checkpointReport && !l.checkpointInsight && !l.checkpointSupport;
          } else {
            // Map snake_case filter key to camelCase Lead field
            const cpMap: Record<string, keyof Lead> = {
              checkpoint_gmc: 'checkpointGmc',
              checkpoint_services: 'checkpointServices',
              checkpoint_report: 'checkpointReport',
              checkpoint_insight: 'checkpointInsight',
              checkpoint_support: 'checkpointSupport',
            };
            const fieldKey = cpMap[sourceFilter];
            matchSource = fieldKey ? !!l[fieldKey] : false;
          }
        } else {
          matchSource = l.source === sourceFilter;
        }
      }

      return matchSearch && matchStage && matchSource;
    });
    data = [...data].sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      // Use numeric date comparison for createdAt to ensure stable ordering
      if (sortCol === 'createdAt') {
        const at = av ? new Date(av as string).getTime() : 0;
        const bt = bv ? new Date(bv as string).getTime() : 0;
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      const avStr = av == null ? '' : String(av);
      const bvStr = bv == null ? '' : String(bv);
      return sortDir === 'asc' ? avStr.localeCompare(bvStr) : bvStr.localeCompare(avStr);
    });
    return data;
  }, [leads, search, stageFilter, sourceFilter, sortCol, sortDir]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const toggleSort = (col: keyof Lead) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: keyof Lead }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} className="text-muted-foreground" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelectedIds(selectedIds.length === paginated.length ? [] : paginated.map((l) => l.id));

  const handleBulkDelete = () => {
    toast.success(`${selectedIds.length} leads removed`);
    setSelectedIds([]);
  };

  const handleBulkAssign = () => {
    toast.success(`${selectedIds.length} leads assigned to Priya Sharma`);
    setSelectedIds([]);
  };

  // Checkpoint API sync handler
  const handleCheckpointSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await fetch('/api/leads/sync-checkpoint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ max_pages: syncMaxPages }),
      });
      let data = await res.json();
      setSyncResult(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(data.message ?? `Synced ${data.total_upserted} leads`);
        refetch();
      }
    } catch {
      toast.error('Failed to connect to MBBS Lighthouse API');
      setSyncResult({ errors: ['Network error — could not reach sync endpoint'] });
    } finally {
      setSyncLoading(false);
    }
  };

  // Auto-sync every 15 minutes
  const syncMaxPagesRef = useRef(syncMaxPages);
  useEffect(() => { syncMaxPagesRef.current = syncMaxPages; }, [syncMaxPages]);

  useEffect(() => {
    // Run once immediately on mount
    const runAutoSync = async () => {
      setSyncLoading(true);
      setSyncResult(null);
      try {
        const res = await fetch('/api/leads/sync-checkpoint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ max_pages: syncMaxPagesRef.current }),
        });
        let data = await res.json();
        setSyncResult(data);
        if (!data.error) {
          setLastAutoSync(new Date());
          refetch();
        }
      } catch {
        // silent fail for auto-sync
      } finally {
        setSyncLoading(false);
        setNextSyncIn(15 * 60);
      }
    };

    runAutoSync();

    // Set up 15-minute interval
    const interval = setInterval(() => {
      runAutoSync();
    }, 15 * 60 * 1000);

    // Countdown timer (updates every second)
    const countdown = setInterval(() => {
      setNextSyncIn((prev) => (prev <= 1 ? 15 * 60 : prev - 1));
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(countdown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBulkEnroll = async () => {
    if (!selectedCampaignId) {
      toast.error('Please select a campaign');
      return;
    }

    setEnrollLoading(true);
    setEnrollResult(null);

    try {
      const selectedLeads = leads.filter((l) => selectedIds.includes(l.id));
      const leadsPayload = selectedLeads.map((l) => ({
        name: l.name,
        phone: l.phone,
        neetScore: l.neetScore,
        stage: l.stage,
        source: l.source,
      }));

      const res = await fetch('/api/twilio/drip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignType: selectedCampaignId,
          leads: leadsPayload,
          sendImmediately: true,
        }),
      });

      let data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to enroll leads');
      }

      const failedImmediate = (data.immediateResults ?? []).filter((r: { success: boolean }) => !r.success);
      const errors: string[] = [];
      if (data.uniqueErrors?.length) errors.push(...data.uniqueErrors);

      setEnrollResult({
        enrolled: data.enrolledLeads,
        queued: data.queuedMessages,
        errors: errors.length ? errors : undefined,
      });

      if (errors.length === 0) {
        toast.success(`${data.enrolledLeads} leads enrolled in campaign. ${data.queuedMessages} follow-up messages queued.`);
        setSelectedIds([]);
        setShowEnrollModal(false);
      } else if (failedImmediate.length < leadsPayload.length) {
        toast.success(`Partially enrolled: ${data.enrolledLeads - failedImmediate.length} succeeded, ${failedImmediate.length} failed.`);
      } else {
        toast.error(`Enrollment failed: ${errors[0]}`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unexpected error during enrollment';
      toast.error(msg);
      setEnrollResult({ errors: [msg] });
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleStageChange = async (lead: Lead, newStage: LeadStage) => {
    const err = await updateLeadStage(lead.id, newStage);
    if (err) {
      toast.error(`Failed to update stage: ${err.message}`);
    } else {
      toast.success(`${lead.name} moved to ${newStage}`);
    }
    setStageDropdownId(null);
  };

  const handleClearAllLeads = async () => {
    setClearLoading(true);
    try {
      const supabase = createClient();
      const { error: deleteError } = await supabase
        .from('leads')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (deleteError) {
        toast.error(`Failed to clear leads: ${deleteError.message}`);
      } else {
        toast.success('All leads have been cleared. You can now import a fresh CSV.');
        refetch();
        setSelectedIds([]);
      }
    } catch (err: unknown) {
      toast.error('An unexpected error occurred while clearing leads.');
    } finally {
      setClearLoading(false);
      setShowClearConfirm(false);
    }
  };

  const handleDeleteDuplicates = async () => {
    setDedupeLoading(true);
    try {
      const supabase = createClient();
      const { data, error: rpcErr } = await supabase.rpc('delete_duplicate_leads');
      if (rpcErr) {
        toast.error(`Failed to delete duplicates: ${rpcErr.message}`);
      } else {
        const count = data as number;
        toast.success(count > 0 ? `Deleted ${count} duplicate lead(s). One record kept per phone number.` : 'No duplicates found.');
        if (count > 0) refetch();
      }
    } catch (err: unknown) {
      toast.error('An unexpected error occurred while deleting duplicates.');
    } finally {
      setDedupeLoading(false);
      setShowDedupeConfirm(false);
    }
  };

  const openAddLead = () => {
    setLeadForm(emptyLeadForm);
    setShowAddLeadModal(true);
  };

  const openEditLead = (lead: Lead) => {
    setLeadForm({
      name: lead.name,
      phone: lead.phone,
      email: lead.email,
      neetScore: String(lead.neetScore),
      source: lead.source,
      stage: lead.stage,
      counselor: lead.counselor === 'Unassigned' ? '' : lead.counselor,
      budget: lead.budget,
      location: lead.location,
    });
    setEditingLead(lead);
  };

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setLeadFormLoading(true);
    const supabase = createClient();
    const payload = {
      name: leadForm.name.trim(),
      phone: formatPhone(leadForm.phone.trim()),
      email: leadForm.email.trim(),
      neet_score: Number(leadForm.neetScore) || 0,
      source: leadForm.source,
      stage: leadForm.stage,
      counselor: leadForm.counselor.trim() || 'Unassigned',
      budget: leadForm.budget.trim(),
      location: leadForm.location.trim() || 'India',
      last_activity: 'Just now',
    };
    try {
      if (editingLead) {
        const { error: updateErr } = await supabase
          .from('leads')
          .update(payload)
          .eq('id', editingLead.id);
        if (updateErr) throw updateErr;
        toast.success(`${leadForm.name} updated successfully`);
        setEditingLead(null);
      } else {
        // Check for duplicate phone before inserting
        const formattedPhone = formatPhone(leadForm.phone.trim());
        const { data: existing, error: checkErr } = await supabase
          .from('leads')
          .select('id, name')
          .eq('phone', formattedPhone)
          .maybeSingle();
        if (checkErr) throw checkErr;
        if (existing) {
          toast.error(`A lead with phone ${formattedPhone} already exists (${existing.name}). Duplicate not added.`);
          setLeadFormLoading(false);
          return;
        }
        const { error: insertErr } = await supabase
          .from('leads')
          .insert([payload]);
        if (insertErr) throw insertErr;
        toast.success(`${leadForm.name} added successfully`);
        setShowAddLeadModal(false);

        // ── Fresh-lead WhatsApp trigger ──────────────────────────────────
        // Call the server-side enroll-fresh API which directly sends the
        // day-0 WhatsApp message via Twilio (no queue dependency).
        try {
          const formattedPhone = formatPhone(leadForm.phone.trim());
          const enrollRes = await fetch('/api/leads/enroll-fresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              lead_name: leadForm.name.trim(),
              lead_phone: formattedPhone,
              neet_score: Number(leadForm.neetScore) || undefined,
              stage: leadForm.stage,
              source: leadForm.source,
            }),
          });

          const enrollData = await enrollRes.json() as {
            success?: boolean;
            sent?: boolean;
            skipped?: boolean;
            accepted?: boolean;
            reason?: string;
            messageSid?: string;
            template?: string;
            campaign?: string;
            futureStepsQueued?: number;
            error?: string;
            deliveryStatus?: string;
            errorCode?: number;
            guidance?: string;
            isSandbox?: boolean;
          };

          console.log('[FreshEnroll] API response:', JSON.stringify(enrollData));

          if (enrollData.skipped) {
            toast.warning(`WhatsApp not triggered: ${enrollData.reason}`);
          } else if (enrollData.sent && enrollData.messageSid) {
            // Real success — message accepted AND delivered
            const statusLabel = enrollData.deliveryStatus ? ` (${enrollData.deliveryStatus})` : '';
            toast.success(`✅ WhatsApp sent to ${leadForm.name} (${enrollData.campaign})${statusLabel}`);
          } else if (enrollData.accepted && enrollData.errorCode) {
            // Twilio accepted the message but delivery will fail — show the real error
            const guidance = enrollData.guidance ?? enrollData.error ?? 'Unknown delivery error';
            if (enrollData.errorCode === 63007 || enrollData.isSandbox) {
              toast.error(
                `📵 WhatsApp not delivered (Sandbox): ${guidance}`,
                { duration: 10000 }
              );
            } else if (enrollData.errorCode === 63016) {
              toast.error(
                `⏰ WhatsApp not delivered (24h window): ${guidance}`,
                { duration: 10000 }
              );
            } else {
              toast.error(
                `❌ WhatsApp delivery failed (error ${enrollData.errorCode}): ${guidance}`,
                { duration: 8000 }
              );
            }
          } else if (enrollData.success === false || enrollData.error) {
            toast.error(`WhatsApp send failed: ${enrollData.error ?? 'Unknown error'}`);
          } else if (!enrollRes.ok) {
            toast.error(`WhatsApp trigger failed (HTTP ${enrollRes.status}): ${enrollData.error ?? 'Unknown error'}`);
          }
        } catch (enrollErr: unknown) {
          const msg = enrollErr instanceof Error ? enrollErr.message : 'Network error';
          toast.error(`WhatsApp trigger error: ${msg}`);
          console.error('[FreshEnroll] Error calling enroll-fresh API:', msg);
        }
        // ────────────────────────────────────────────────────────────────
      }
      refetch();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save lead';
      toast.error(msg);
    } finally {
      setLeadFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Lead Management</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Loading leads…</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-12 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Connecting to live data…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-bold text-foreground">Lead Management</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-sm text-danger font-medium">Failed to load leads: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Lead Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {filtered.length} leads · NEET 2026 Season
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-success font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse inline-block" />
              Live
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
            <Download size={15} />
            Export CSV
          </button>
          {/* Checkpoint Sync Button */}
          <button
            onClick={() => { setShowSyncModal(true); setSyncResult(null); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors active:scale-95"
          >
            <RefreshCw size={15} className={syncLoading ? 'animate-spin' : ''} />
            {syncLoading ? 'Syncing…' : 'Sync Checkpoint Leads'}
            {!syncLoading && (
              <span className="ml-1 text-xs bg-emerald-500 text-white rounded-full px-1.5 py-0.5 font-mono">
                {Math.floor(nextSyncIn / 60)}:{String(nextSyncIn % 60).padStart(2, '0')}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowSourceModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            <Link2 size={15} />
            Import Leads
          </button>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors active:scale-95"
          >
            <Trash2 size={15} />
            Clear All Leads
          </button>
          <button
            onClick={() => setShowDedupeConfirm(true)}
            disabled={dedupeLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors active:scale-95 disabled:opacity-60"
          >
            {dedupeLoading ? <Loader2 size={15} className="animate-spin" /> : <AlertTriangle size={15} />}
            {dedupeLoading ? 'Removing…' : 'Delete Duplicates'}
          </button>
          <button
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors active:scale-95"
            onClick={openAddLead}
          >
            <UserPlus size={15} />
            Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {SOURCES.map((s) => (
              <option key={`src-${s.value}`} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          {(['All', ...STAGES] as const).map((s) => (
            <button
              key={`stage-chip-${s}`}
              onClick={() => { setStageFilter(s as LeadStage | 'All'); setPage(1); }}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                stageFilter === s
                  ? 'bg-primary text-white border-primary' :'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        {/* Checkpoint quick-filter chips */}
        <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
          <span className="text-xs text-muted-foreground self-center font-medium">Checkpoints:</span>
          {[
            { key: 'checkpoint_fresh', label: 'Fresh' },
            { key: 'checkpoint_gmc', label: 'GMC' },
            { key: 'checkpoint_services', label: 'Services' },
            { key: 'checkpoint_report', label: 'Report' },
            { key: 'checkpoint_insight', label: 'Insight' },
            { key: 'checkpoint_support', label: 'Support' },
          ].map((cp) => (
            <button
              key={cp.key}
              onClick={() => { setSourceFilter(sourceFilter === cp.key ? 'All Sources' : cp.key); setPage(1); }}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                sourceFilter === cp.key
                  ? cp.key === 'checkpoint_fresh' ?'bg-sky-500 text-white border-sky-500' :'bg-emerald-600 text-white border-emerald-600'
                  : cp.key === 'checkpoint_fresh' ?'bg-background text-muted-foreground border-border hover:border-sky-400/50 hover:text-sky-500' :'bg-background text-muted-foreground border-border hover:border-emerald-500/50 hover:text-emerald-600'
              }`}
            >
              {cp.key === 'checkpoint_fresh' ? '✦' : '✓'} {cp.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 animate-in slide-in-from-bottom-2">
          <span className="text-sm font-semibold text-primary">{selectedIds.length} selected</span>
          <button onClick={handleBulkAssign} className="text-xs font-medium px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
            Assign to Counselor
          </button>
          <button
            onClick={() => { setShowEnrollModal(true); setEnrollResult(null); setSelectedCampaignId(''); }}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            <Zap size={12} />
            Enroll in Campaign
          </button>
          <button onClick={handleBulkDelete} className="text-xs font-medium px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors">
            Delete Selected
          </button>
          <button onClick={() => setSelectedIds([])} className="ml-auto text-muted-foreground hover:text-foreground">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === paginated.length && paginated.length > 0}
                    onChange={toggleAll}
                    className="rounded border-border"
                  />
                </th>
                {[
                  { label: 'Name', col: 'name' as keyof Lead },
                  { label: 'Phone', col: 'phone' as keyof Lead },
                  { label: 'NEET Score', col: 'neetScore' as keyof Lead },
                  { label: 'Source', col: 'source' as keyof Lead },
                  { label: 'Stage', col: 'stage' as keyof Lead },
                  { label: 'Counselor', col: 'counselor' as keyof Lead },
                  { label: 'Budget', col: 'budget' as keyof Lead },
                  { label: 'Location', col: 'location' as keyof Lead },
                  { label: 'Last Activity', col: 'lastActivity' as keyof Lead },
                ].map((h) => (
                  <th
                    key={`th-${h.col}`}
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground select-none"
                    onClick={() => toggleSort(h.col)}
                  >
                    <span className="flex items-center gap-1">
                      {h.label}
                      <SortIcon col={h.col} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Checkpoints
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((lead, idx) => {
                const checkpoints = [
                  { key: 'checkpointGmc' as keyof Lead, label: 'GMC' },
                  { key: 'checkpointServices' as keyof Lead, label: 'Svc' },
                  { key: 'checkpointReport' as keyof Lead, label: 'Rpt' },
                  { key: 'checkpointInsight' as keyof Lead, label: 'Ins' },
                  { key: 'checkpointSupport' as keyof Lead, label: 'Sup' },
                ].filter((cp) => lead[cp.key] !== null && lead[cp.key] !== undefined && lead[cp.key] !== '');

                return (
                  <tr
                    key={lead.id}
                    className={`border-b border-border hover:bg-muted/30 transition-colors ${
                      idx % 2 === 0 ? '' : 'bg-muted/10'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(lead.id)}
                        onChange={() => toggleSelect(lead.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                          {lead.name.split(' ').map((n) => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{lead.name}</p>
                          <p className="text-xs text-muted-foreground">{lead.email}</p>
                        </div>
                        {lead.duplicate && (
                          <span title="Possible duplicate" className="inline-flex items-center gap-0.5 text-[10px] font-bold text-warning bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                            <AlertTriangle size={9} /> DUP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">{formatPhone(lead.phone)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold tabular-nums text-sm ${
                        lead.neetScore >= 600 ? 'text-success' :
                        lead.neetScore >= 500 ? 'text-amber-600' : 'text-danger'
                      }`}>
                        {lead.neetScore}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <SourceBadge source={lead.source} />
                    </td>
                    <td className="px-4 py-3 relative">
                      <button
                        onClick={() => setStageDropdownId(stageDropdownId === lead.id ? null : lead.id)}
                        className="flex items-center gap-1 group"
                      >
                        <StageBadge stage={lead.stage} />
                        <ChevronDown size={12} className="text-muted-foreground group-hover:text-foreground" />
                      </button>
                      {stageDropdownId === lead.id && (
                        <div className="absolute z-20 top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-48">
                          {STAGES.map((s) => (
                            <button
                              key={`dd-${lead.id}-${s}`}
                              className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                              onClick={() => handleStageChange(lead, s)}
                            >
                              {s === lead.stage && <Check size={12} className="text-primary" />}
                              <span className={s === lead.stage ? 'font-semibold text-primary' : 'text-foreground'}>
                                {s}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{lead.counselor}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{lead.budget}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        lead.location === 'India' ? 'bg-primary/10 text-primary' : 'bg-purple-50 text-purple-700'
                      }`}>
                        {lead.location}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{lead.lastActivity}</td>
                    {/* Checkpoint badges */}
                    <td className="px-4 py-3">
                      {checkpoints.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {checkpoints.map((cp) => (
                            <span
                              key={cp.key}
                              title={`Reached ${cp.label} checkpoint`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                            >
                              {cp.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-50 text-sky-600 border border-sky-200">Fresh</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          title="View lead"
                          onClick={() => toast.info(`Opening ${lead.name}'s profile`)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          title="Send WhatsApp"
                          onClick={() => toast.success(`WhatsApp opened for ${lead.name}`)}
                          className="p-1.5 rounded-lg hover:bg-green-50 text-muted-foreground hover:text-green-600 transition-colors"
                        >
                          <MessageCircle size={14} />
                        </button>
                        <button
                          title="Edit lead"
                          onClick={() => openEditLead(lead)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          title="Delete lead — cannot be undone"
                          onClick={() => toast.error(`${lead.name} deleted`)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-danger transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length} leads
          </p>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-2.5 py-1.5 text-xs font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={`page-${p}`}
                onClick={() => setPage(p)}
                className={`w-8 h-8 text-xs font-medium rounded-lg transition-colors ${
                  p === page
                    ? 'bg-primary text-white' :'border border-border hover:bg-muted text-muted-foreground'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
              className="px-2.5 py-1.5 text-xs font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showSourceModal && (
        <LeadSourceModal
          onClose={() => setShowSourceModal(false)}
          onImported={() => { refetch(); setShowSourceModal(false); }}
        />
      )}

      {/* Checkpoint Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <RefreshCw size={16} className="text-emerald-600" />
                <h2 className="text-sm font-semibold text-foreground">Sync Leads from Checkpoint API</h2>
              </div>
              <button onClick={() => setShowSyncModal(false)} disabled={syncLoading} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <X size={15} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 space-y-1">
                <p className="text-xs font-semibold text-emerald-800">MBBS Lighthouse API</p>
                <p className="text-xs text-emerald-700 font-mono break-all">api.mbbslighthouse.com/crm/req/v1/crm/get/users</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Downloads leads from the MBBS Lighthouse system and syncs their checkpoint data (GMC, Services, Report, Insight, Support) into this CRM. Existing leads are updated — no duplicates created.
              </p>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Max Pages to Sync <span className="text-muted-foreground font-normal">({syncMaxPages * 50} leads max)</span>
                </label>
                <select
                  value={syncMaxPages}
                  onChange={(e) => setSyncMaxPages(Number(e.target.value))}
                  disabled={syncLoading}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                >
                  <option value={1}>1 page — 50 leads</option>
                  <option value={5}>5 pages — 250 leads</option>
                  <option value={10}>10 pages — 500 leads</option>
                  <option value={20}>20 pages — 1,000 leads</option>
                  <option value={50}>50 pages — 2,500 leads</option>
                  <option value={100}>100 pages — All leads (~3,800+)</option>
                </select>
              </div>

              {syncResult && (
                <div className={`rounded-lg px-3 py-2.5 space-y-1 ${syncResult.errors?.length ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  {syncResult.message && (
                    <p className={`text-xs font-semibold ${syncResult.errors?.length ? 'text-red-700' : 'text-green-700'}`}>
                      {syncResult.message}
                    </p>
                  )}
                  {syncResult.total_fetched !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      Fetched: {syncResult.total_fetched} · Upserted: {syncResult.total_upserted} · Pages: {syncResult.pages_fetched}
                    </p>
                  )}
                  {syncResult.errors?.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{e}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                onClick={() => setShowSyncModal(false)}
                disabled={syncLoading}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleCheckpointSync}
                disabled={syncLoading}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60 active:scale-95"
              >
                {syncLoading ? (
                  <><Loader2 size={14} className="animate-spin" />Syncing…</>
                ) : (
                  <><RefreshCw size={14} />Start Sync</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-accent" />
                <h2 className="text-sm font-semibold text-foreground">Enroll in Drip Campaign</h2>
              </div>
              <button onClick={() => setShowEnrollModal(false)} disabled={enrollLoading} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <X size={15} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Enrolling <span className="font-semibold text-foreground">{selectedIds.length} lead{selectedIds.length !== 1 ? 's' : ''}</span> into a drip campaign will send the Day 0 message immediately and queue all follow-up messages automatically.
              </p>
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Select Campaign <span className="text-red-500">*</span></label>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  disabled={enrollLoading || dripCampaignsLoading}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors disabled:opacity-50"
                >
                  <option value="">— Choose a campaign —</option>
                  {dripCampaignsLoading ? (
                    <option disabled>Loading campaigns…</option>
                  ) : (
                    dripCampaigns.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))
                  )}
                </select>
              </div>
              {enrollResult?.errors && enrollResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 space-y-1">
                  <p className="text-xs font-semibold text-red-700">Errors encountered:</p>
                  {enrollResult.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{e}</p>
                  ))}
                </div>
              )}
              {enrollResult?.enrolled !== undefined && !enrollResult.errors?.length && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                  <p className="text-xs font-semibold text-green-700">
                    ✓ {enrollResult.enrolled} leads enrolled · {enrollResult.queued ?? 0} follow-up messages queued
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                onClick={() => setShowEnrollModal(false)}
                disabled={enrollLoading}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEnroll}
                disabled={enrollLoading || !selectedCampaignId}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-60 active:scale-95"
              >
                {enrollLoading ? (
                  <><Loader2 size={14} className="animate-spin" />Enrolling…</>
                ) : (
                  <><Zap size={14} />Enroll {selectedIds.length} Lead{selectedIds.length !== 1 ? 's' : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deduplicate Confirm Modal */}
      {showDedupeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Delete Duplicate Leads?</h3>
                <p className="text-xs text-muted-foreground mt-0.5">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              For each phone number with multiple leads, <strong>all duplicates will be deleted</strong> and only the <strong>oldest record</strong> will be kept.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDedupeConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDuplicates}
                disabled={dedupeLoading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-60"
              >
                {dedupeLoading ? 'Deleting…' : 'Yes, Delete Duplicates'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Leads Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-danger" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Clear All Leads</h2>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-2">
              You are about to permanently delete <span className="font-bold text-danger">{leads.length.toLocaleString()} leads</span> from the system.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              All lead data, including contact information, stages, and history will be removed. This is typically done before a fresh CSV import.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearLoading}
                className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAllLeads}
                disabled={clearLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors disabled:opacity-50 active:scale-95"
              >
                {clearLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Clearing…
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Yes, Clear All Leads
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Lead Modal */}
      {(showAddLeadModal || editingLead) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <UserPlus size={16} className="text-primary" />
                <h2 className="text-sm font-semibold text-foreground">
                  {editingLead ? `Edit Lead — ${editingLead.name}` : 'Add New Lead'}
                </h2>
              </div>
              <button
                onClick={() => { setShowAddLeadModal(false); setEditingLead(null); }}
                disabled={leadFormLoading}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X size={15} />
              </button>
            </div>
            <form onSubmit={handleLeadFormSubmit}>
              <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Arjun Sharma"
                      required
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Phone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="e.g. 9876543210"
                      required
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Email</label>
                    <input
                      type="email"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="e.g. arjun@email.com"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">NEET Score</label>
                    <input
                      type="number"
                      min={0}
                      max={720}
                      value={leadForm.neetScore}
                      onChange={(e) => setLeadForm((f) => ({ ...f, neetScore: e.target.value }))}
                      placeholder="e.g. 580"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Source</label>
                    <select
                      value={leadForm.source}
                      onChange={(e) => setLeadForm((f) => ({ ...f, source: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="Neet Navigator">Neet Navigator</option>
                      <option value="MBBS Lighthouse">MBBS Lighthouse</option>
                      <option value="GMC">GMC</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Stage</label>
                    <select
                      value={leadForm.stage}
                      onChange={(e) => setLeadForm((f) => ({ ...f, stage: e.target.value as LeadStage }))}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Counselor</label>
                    <input
                      type="text"
                      value={leadForm.counselor}
                      onChange={(e) => setLeadForm((f) => ({ ...f, counselor: e.target.value }))}
                      placeholder="e.g. Priya Sharma"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Budget</label>
                    <input
                      type="text"
                      value={leadForm.budget}
                      onChange={(e) => setLeadForm((f) => ({ ...f, budget: e.target.value }))}
                      placeholder="e.g. ₹50L–₹70L"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Location</label>
                    <input
                      type="text"
                      value={leadForm.location}
                      onChange={(e) => setLeadForm((f) => ({ ...f, location: e.target.value }))}
                      placeholder="e.g. India"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => { setShowAddLeadModal(false); setEditingLead(null); }}
                  disabled={leadFormLoading}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={leadFormLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95"
                >
                  {leadFormLoading ? (
                    <><Loader2 size={14} className="animate-spin" />{editingLead ? 'Saving…' : 'Adding…'}</>
                  ) : (
                    <><UserPlus size={14} />{editingLead ? 'Save Changes' : 'Add Lead'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}